import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Mail, ShieldCheck } from "lucide-react";
import { LanguageSwitch } from "./NavbarTerritorialeTop";
import ProtocolloContattoModal from "../protocollo/ProtocolloContattoModal";
import { subscribeNewsletter } from "../../api";
import { NEWSLETTER_SUBSCRIBE_ENABLED } from "../../config/featureFlags";
import { useLang } from "../../LangContext";

/* BLOCCO C: etichette via t("footx.*") — traduzioni in i18n.js (da rivedere madrelingua). */
const COLUMNS = [
  {
    id: "piazza",
    tKey: "footx.col_piazza",
    links: [
      { to: "/piazze", tKey: "footx.l_piazze" },
      { to: "/eventi", tKey: "footx.l_eventi" },
      { to: "/manifesto", tKey: "footx.l_manifesto" },
      { to: "/regole-piazza", tKey: "footx.l_regole" },
    ],
  },
  {
    id: "mappe",
    tKey: "footx.col_mappe",
    links: [
      { to: "/esplora-la-mappa", tKey: "footx.l_mappa3d" },
      { to: "/mappa-del-mondo", tKey: "footx.l_mappamondo" },
      { to: "/cammini", tKey: "footx.l_cammini" },
      { to: "/qr", tKey: "footx.l_arqr" },
      { to: "/qr", tKey: "footx.l_guideqr" },
    ],
  },
  {
    id: "mondo",
    tKey: "footx.col_mondo",
    links: [
      { to: "/gemellaggi", tKey: "footx.l_gemellaggi" },
      { to: "/comuni-amici", tKey: "footx.l_comuniamici" },
      { to: "/origini", tKey: "footx.l_origini" },
      { to: "/universita", tKey: "footx.l_universita" },
    ],
  },
  {
    id: "enti",
    tKey: "footx.col_enti",
    links: [
      { to: "/per-comuni", tKey: "footx.l_percomuni" },
      { to: "/governance", tKey: "footx.l_governance" },
      { to: "/stanza-sindaci", tKey: "footx.l_sindaci" },
      { to: "/ufficio-stampa", tKey: "footx.l_stampa" },
    ],
  },
];

const LEGAL = [
  { to: "/chi-siamo", tKey: "footx.leg_chisiamo" },
  { to: "/privacy", tKey: "footx.leg_privacy" },
  { to: "/cookie-policy", tKey: "footx.leg_cookie" },
  { to: "/termini", tKey: "footx.leg_termini" },
];

const LINK_CLS =
  "min-h-[48px] md:min-h-[36px] inline-flex items-center text-base text-[#0F172A] hover:text-[#a85837] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#a85837] rounded";

function FooterColumn({ col, t }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-[#eedfcb] md:border-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={`footer-col-${col.id}`}
        className="w-full min-h-[48px] flex items-center justify-between md:hidden text-left font-serif font-bold text-[#162D4A]"
      >
        {t(col.tKey)}
        <ChevronDown size={18} className={`transition-transform ${open ? "rotate-180" : ""}`} aria-hidden />
      </button>
      <h5 className="hidden md:block font-serif font-bold text-[#162D4A] text-sm uppercase tracking-widest border-b border-[#D4AF37] pb-1 mb-2">
        {t(col.tKey)}
      </h5>
      <ul
        id={`footer-col-${col.id}`}
        className={`${open ? "block" : "hidden"} md:block pb-4 md:pb-0`}
      >
        {col.links.map((l) => (
          <li key={l.to + l.tKey}>
            <Link to={l.to} className={LINK_CLS}>
              {t(l.tKey)}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

const NL_LANGS = ["it", "en", "fr", "es"];

export default function FooterTerritorialeCompleto() {
  const { t, lang } = useLang();
  const [nlEmail, setNlEmail] = useState("");
  const [nlLang, setNlLang] = useState(NL_LANGS.includes(lang) ? lang : "it");
  const [nlConsent, setNlConsent] = useState(false);
  const [nlDone, setNlDone] = useState(false);
  const [nlErr, setNlErr] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);

  useEffect(() => {
    if (NL_LANGS.includes(lang)) setNlLang(lang);
  }, [lang]);

  const submitNl = async (e) => {
    e.preventDefault();
    const email = nlEmail.trim();
    if (!email || !nlConsent) return;
    try {
      await subscribeNewsletter({ email, lang: nlLang, consent: true });
      setNlDone(true);
      setNlErr(false);
    } catch {
      try {
        localStorage.setItem("6amico_cartolina_domenica_draft", email);
      } catch { /* noop */ }
      setNlDone(false);
      setNlErr(true);
    }
  };

  return (
    <footer
      className="bg-[#f7efe6] border-t border-[#eedfcb] text-[#0F172A]"
      data-testid="footer-territoriale"
    >
      <div className="max-w-[1200px] mx-auto px-4 md:px-8 py-12">
        {/* A) Fascia alta: logo + payoff | CTA + email */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5">
          <div className="flex items-center gap-3">
            <span
              className="w-11 h-11 shrink-0 rounded-full bg-[#c26d4a] text-white font-serif font-bold text-lg flex items-center justify-center border-2 border-[#a85837]"
              aria-hidden
            >
              6a
            </span>
            <span className="font-serif font-bold text-xl text-[#0F172A] whitespace-nowrap">6AMICO</span>
            <span className="text-sm text-[#0F172A] whitespace-nowrap hidden sm:inline">
              {t("footx.payoff")}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Link
              to="/censisci"
              className="inline-flex items-center justify-center min-h-[48px] whitespace-nowrap rounded-full bg-[#c26d4a] hover:bg-[#a85837] text-white font-sans font-semibold text-sm px-[20px] py-[12px] transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#a85837]"
              data-testid="footer-cta-censisci"
            >
              {t("footx.cta_censisci")}
            </Link>
            <button
              type="button"
              onClick={() => setContactOpen(true)}
              className="inline-flex items-center gap-2 min-h-[48px] rounded-full border border-[#ecd9c2] bg-[#fdfbf7] px-4 py-2 text-sm font-sans font-semibold text-[#0F172A] hover:border-[#c26d4a] hover:text-[#a85837] transition-colors"
              data-testid="footer-mail-info"
              aria-label="Scrivici: info@6amico.com"
            >
              <Mail size={14} className="text-[#a85837] shrink-0" aria-hidden />
              info@6amico.com
            </button>
          </div>
        </div>

        {/* B) Fascia centrale: 4 colonne uguali */}
        <div className="mt-10 grid grid-cols-1 md:grid-cols-4 gap-2 md:gap-8">
          {COLUMNS.map((c) => (
            <FooterColumn key={c.id} col={c} t={t} />
          ))}
        </div>

        {/* C) Fascia newsletter: riquadro orizzontale a tutta larghezza */}
        <form
          onSubmit={submitNl}
          id="cartolina-domenica"
          className="mt-10 scroll-mt-28 rounded-2xl border border-[#E2E8F0] bg-[#FFFFFF] p-6"
        >
          {!NEWSLETTER_SUBSCRIBE_ENABLED ? (
            <>
              <p className="font-serif font-bold text-lg text-[#0F172A]">
                {t("footx.nl_title")}
              </p>
              <p className="text-sm font-sans text-[#0F172A] mt-1" data-testid="footer-nl-soon">
                {t("footx.nl_soon")}
              </p>
            </>
          ) : (
          <>
          <div className="flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-8">
            <div className="lg:shrink-0">
              <label
                htmlFor="footer-nl"
                className="block font-serif font-bold text-lg text-[#0F172A]"
              >
                {t("footx.nl_title")}
              </label>
              <p className="text-sm font-sans text-[#0F172A] mt-0.5">
                {t("footx.nl_tagline")}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 lg:ml-auto">
              <input
                id="footer-nl"
                type="email"
                value={nlEmail}
                onChange={(e) => {
                  setNlEmail(e.target.value);
                  setNlDone(false);
                  setNlErr(false);
                }}
                placeholder={t("footx.nl_placeholder")}
                className="w-full sm:w-64 min-h-[48px] rounded-full border border-[#E2E8F0] bg-white px-4 text-base text-[#0F172A]"
              />
              <select
                value={nlLang}
                onChange={(e) => setNlLang(e.target.value)}
                aria-label={t("footx.nl_lang")}
                className="min-h-[48px] w-full sm:w-[72px] rounded-full border border-[#E2E8F0] bg-white px-2 text-sm font-semibold text-[#0F172A]"
                data-testid="footer-nl-lang"
              >
                {NL_LANGS.map((l) => (
                  <option key={l} value={l}>{l.toUpperCase()}</option>
                ))}
              </select>
              <button
                type="submit"
                disabled={!nlConsent || !nlEmail.trim()}
                className="min-h-[48px] whitespace-nowrap rounded-full bg-[#162D4A] hover:bg-[#0F1F33] text-[#FFFFFF] font-sans font-semibold text-base px-6 flex items-center justify-center gap-2 disabled:bg-[#162D4A]/50 disabled:hover:bg-[#162D4A]/50 disabled:cursor-not-allowed transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#a85837]"
                data-testid="footer-nl-submit"
              >
                {t("footx.nl_subscribe")}
              </button>
            </div>
          </div>
          <label className="mt-3 flex items-center gap-2 cursor-pointer text-sm font-sans text-[#0F172A]">
            <input
              type="checkbox"
              checked={nlConsent}
              onChange={(e) => setNlConsent(e.target.checked)}
              className="w-4 h-4 shrink-0 accent-[#a85837]"
              data-testid="footer-nl-consent"
            />
            <span className="whitespace-normal">
              {t("footx.nl_consent_pre")}{" "}
              <Link to="/privacy" className="underline hover:text-[#a85837]">
                {t("footx.nl_consent_link")}
              </Link>
              {t("footx.nl_consent_post")}
            </span>
          </label>
          {nlDone && (
            <p className="mt-2 text-sm font-sans font-semibold text-[#a85837]" role="status">
              {t("footx.nl_pending")}
            </p>
          )}
          {nlErr && (
            <p className="mt-2 text-sm font-sans font-semibold text-terracotta" role="alert">
              {t("footx.nl_error")}
            </p>
          )}
          </>
          )}
        </form>
      </div>

      {/* D) Riga legale finale */}
      <div className="border-t border-[#E2E8F0] bg-[#fdfbf7]">
        {/* pb su mobile + pr su desktop: la zona resta libera dal FAB chat (BLOCCO H) */}
        <div className="max-w-[1200px] mx-auto px-4 md:px-8 pt-4 pb-24 md:py-4 md:pr-44 flex flex-col items-center gap-3">
          <nav className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1" aria-label="Link legali">
            <button
              type="button"
              onClick={() => setContactOpen(true)}
              className="min-h-[44px] inline-flex items-center gap-1.5 text-[13px] font-sans font-semibold text-[#0F172A] hover:text-[#a85837] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#a85837] rounded"
            >
              <Mail size={13} className="text-[#a85837]" aria-hidden />
              info@6amico.com
            </button>
            {LEGAL.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="min-h-[44px] inline-flex items-center text-[13px] font-sans font-semibold text-[#0F172A] hover:text-[#a85837] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#a85837] rounded"
              >
                {t(l.tKey)}
              </Link>
            ))}
          </nav>
          <div className="w-full flex flex-col md:flex-row md:items-center md:justify-between gap-2">
            <LanguageSwitch compact />
            <p className="footer-legal-line text-[13px] font-sans text-[#0F172A] text-center md:flex-1 leading-relaxed">
              © 2013-2026 6amico.com · Evoluzione tecnologica del Social Network "Aggiungimiaipreferiti" (Reg. SIAE n. 003099 del 03/03/2011) · Marchio Storico di Fatto e di Preuso · Fondatore e Autore: <span className="whitespace-nowrap">Paolo Pinti</span> · Tutti i diritti riservati ai sensi della Legge n. 633/1941 · Conforme DSA &amp; AI Act.
            </p>
            <span className="inline-flex items-center justify-center gap-1.5 px-3 min-h-[32px] rounded-full border border-[#ecd9c2] bg-[#f7efe6] text-xs font-sans font-bold uppercase tracking-wider text-[#0F172A] shrink-0">
              <ShieldCheck size={12} className="text-[#a85837]" aria-hidden />
              DSA &amp; AI Act Conforme
            </span>
          </div>
        </div>
      </div>
      <ProtocolloContattoModal
        open={contactOpen}
        onClose={() => setContactOpen(false)}
        oggettoPreset="Richiesta informazioni — 6amico.com"
      />
    </footer>
  );
}
